import { MDBContainer, MDBIcon } from "mdbreact";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AppContainer } from "../../../containers/App";
import { CampaignContainer } from "../../../containers/Campaign";
import { ErrorContainer } from "../../../containers/Error";
import { PointCardContainer } from "../../../containers/PointCard";
import { UserContainer } from "../../../containers/User";
import formatDate from "../../../utilities/DateFormatter";
import { handelExternalLinkClick } from "../../../utilities/WespoUtil";
import Accordion from "../../elements/accordion/Accordion";
import CampaignImage from "../../elements/campaign-image/CampaignImage";
import CommonButton from "../../elements/common-button/CommonButton";
import Description from "../../elements/description/Description";
import classes from "./EntryPage.module.scss";

/**
 * エントリー画面コンポーネント
 */
const EntryPage: React.FC = () => {
  const history = useHistory();
  const appContainer = AppContainer.useContainer();
  const errorContainer = ErrorContainer.useContainer();
  const userContainer = UserContainer.useContainer();
  const campaignContainer = CampaignContainer.useContainer();
  const pointCardContainer = PointCardContainer.useContainer();

  const user = userContainer.user;
  const userId = user.id || user.rawId;

  const campaign = campaignContainer.campaign;
  const campaignId = campaignContainer.campaignId;

  const isReached = campaignContainer.isReached;

  const pointCard = pointCardContainer.pointCard;
  const currentCharge = pointCard?.charge || 0;
  const isCompleted = (campaign?.setting.targetAmount || 0) <= currentCharge;

  const format = "YYYY年MM月DD日 (AA)";
  const startDate = formatDate(format, campaign?.outline.period.from);
  const endDate = formatDate(format, campaign?.outline.period.to);

  if (!appContainer.context.loading) {
    if (isReached === undefined) {
      // キャンペーン達成者数を確認
      appContainer.setLoading(true);
      campaignContainer
        .checkAchievedParticipants(campaignId)
        .then(() => {
          appContainer.setLoading(false);
        })
        .catch((error) => errorContainer.setDefaultError(userId, error));
    }
  }

  useEffect(() => {
    // キャンペーン上限に達した場合はエラー画面に遷移
    if (
      (!pointCard?.isEntry || !isCompleted) &&
      (isReached || pointCard?.updateResult === "SKIP")
    ) {
      errorContainer.setCustomError(
        "上限に達しました",
        [
          "ご好評につき、当キャンペーンは",
          "上限に達したため",
          "終了いたしました。",
          "次回キャンペーンへのご参加を",
          "お待ちしております。",
        ],
        userId,
        false
      );
    }
  }, [errorContainer, isCompleted, isReached, pointCard, userId]);

  /**
   * 開始ボタンクリック時の処理
   */
  const handleStartButtonClick = () => {
    if (!user.id) {
      // 未認証状態の場合
      errorContainer.setCustomError(
        "ポイントカード未登録",
        [
          "アカウントを特定できませんでした。",
          "キャンペーン対象施設のポイントカードを",
          "登録・新規発行してから",
          "再度アクセスしてください。",
        ],
        userId
      );
      return;
    }

    history.push(`/${campaignId}/point-card/${user.rawId}`);
  };

  /**
   * LPリンクボタンクリック時の処理
   */
  const handleLinkClick = () => {
    if (!campaign?.assets.landingPageLink) {
      return;
    }

    handelExternalLinkClick(null, campaign.assets.landingPageLink);
  };

  return (
    <MDBContainer fluid>
      <div className="py-3">
        <div className={classes.content}>
          <CampaignImage image={campaign?.assets.mainBanner}></CampaignImage>
        </div>

        <div className={classes.content}>
          {campaign?.messages.description ? (
            <div className="pb-4 px-3">
              <Description
                message={campaign.messages.description}
              ></Description>
            </div>
          ) : null}

          <div className={`px-3 pb-3 ${classes.outline}`}>
            <div className={classes.outlineContent}>
              <div className={classes.titleBlock}>
                <span className={classes.title}>実施時期</span>
              </div>
              <div>
                {startDate} ～ {endDate}
              </div>
            </div>
            {/* <div className={classes.outlineContent}>
              <div className={classes.titleBlock}>
                <span className={classes.title}>対象店舗</span>
              </div>
              <div>{campaign?.outline.targetShops}</div>
            </div> */}
          </div>

          {campaign?.notices.overall ? (
            <div className="px-3">
              <Accordion
                title={"注意事項（必ずお読みください）"}
                contents={campaign.notices.overall}
                bgColor={"lightyellow"}
              ></Accordion>
            </div>
          ) : null}
        </div>

        <div className="px-3">
          {campaign?.status === "preparing" ? (
            <div className={classes.inactiveMessage}>
              本キャンペーンは {startDate} より開始します。
              キャンペーン開始までお待ち下さい。
            </div>
          ) : null}
          {campaign?.status === "end" ? (
            <div className={classes.inactiveMessage}>
              {campaign.notices.finishedDescription
                ? campaign.notices.finishedDescription.map((v, i) => {
                    return <div key={i}>{v}</div>;
                  })
                : "本キャンペーンは終了しました。 またのご参加をお待ちしております。"}
            </div>
          ) : null}
          <div className="pb-3">
            <CommonButton
              fullWidth
              disabled={
                // キャンペーン開始前の場合
                campaign?.status === "preparing" ||
                // キャンペーン終了後 かつ 未エントリーの場合
                (campaign?.status === "end" && !pointCard?.isEntry)
              }
              onClick={handleStartButtonClick}
            >
              {pointCard?.isEntry ? (
                <span>
                  ご利用状況を確認する
                  <MDBIcon icon="angle-right" className="ml-2" />
                </span>
              ) : (
                <span>キャンペーン参加はこちら</span>
              )}
            </CommonButton>
          </div>
          <div>
            <CommonButton fullWidth outline onClick={handleLinkClick}>
              <span>キャンペーン概要はこちら</span>
            </CommonButton>
          </div>
        </div>
      </div>
    </MDBContainer>
  );
};

export default EntryPage;
